import { getTractionBatteryOrderBlockReason, TractionBatteryOrderBlockReason } from './checkUserRights';

export type TractionBatteryUserRoles = Parameters<typeof getTractionBatteryOrderBlockReason>[0];

export const TRACTION_BATTERY_VIN_NON_COMPLIANT_DEFAULT_MESSAGE =
  'Vehicle non-compliant: Please identify the car with a valid VIN to order the traction battery';

export const TRACTION_BATTERY_VIN_NON_COMPLIANT_ERROR_CODE = 'TRACTION_BATTERY_VIN_NON_COMPLIANT';
export const TRACTION_BATTERY_VIN_REQUIRED_ERROR_CODE = 'TRACTION_BATTERY_VIN_REQUIRED';
export const TRACTION_BATTERY_ORDER_NOT_AUTHORIZED_ERROR_CODE = 'TRACTION_BATTERY_ORDER_NOT_AUTHORIZED';
export const TRACTION_BATTERY_ZE_READY_REQUIRED_ERROR_CODE = 'TRACTION_BATTERY_ZE_READY_REQUIRED';

export const isTractionBatteryZeReadyRequiredErrorCode = (errorCode?: string): boolean =>
  errorCode === TRACTION_BATTERY_ZE_READY_REQUIRED_ERROR_CODE;

export const hasTractionBatteryZeReadyRequiredError = (errorRows: Array<{ errorCode?: string }> = []): boolean =>
  errorRows.some((errorRow) => isTractionBatteryZeReadyRequiredErrorCode(errorRow.errorCode));

export const isTractionBatteryVinNonCompliantErrorCode = (errorCode?: string): boolean =>
  errorCode === TRACTION_BATTERY_VIN_NON_COMPLIANT_ERROR_CODE;

export const hasTractionBatteryVinNonCompliantError = (errorRows: Array<{ errorCode?: string }> = []): boolean =>
  errorRows.some((errorRow) => isTractionBatteryVinNonCompliantErrorCode(errorRow.errorCode));

export const isTractionBatteryVinRequiredErrorCode = (errorCode?: string): boolean =>
  errorCode === TRACTION_BATTERY_VIN_REQUIRED_ERROR_CODE;

export const hasTractionBatteryVinRequiredError = (errorRows: Array<{ errorCode?: string }> = []): boolean =>
  errorRows.some((errorRow) => isTractionBatteryVinRequiredErrorCode(errorRow.errorCode));

export const isTractionBatteryOrderNotAuthorizedErrorCode = (errorCode?: string): boolean =>
  errorCode === TRACTION_BATTERY_ORDER_NOT_AUTHORIZED_ERROR_CODE;

export const hasTractionBatteryOrderNotAuthorizedError = (errorRows: Array<{ errorCode?: string }> = []): boolean =>
  errorRows.some((errorRow) => isTractionBatteryOrderNotAuthorizedErrorCode(errorRow.errorCode));

export const getTractionBatteryUploadErrorMessages = (
  errorRows: Array<{ errorCode?: string }> = [],
): TractionBatteryBlockedMessage[] => {
  const messages: TractionBatteryBlockedMessage[] = [];

  if (hasTractionBatteryZeReadyRequiredError(errorRows)) {
    const message = getTractionBatteryBlockedMessage('ze_ready_required');
    if (message) {
      messages.push(message);
    }
  }

  if (hasTractionBatteryVinRequiredError(errorRows)) {
    const message = getTractionBatteryBlockedMessage('vin_required');
    if (message) {
      messages.push(message);
    }
  }

  if (hasTractionBatteryOrderNotAuthorizedError(errorRows)) {
    const message = getTractionBatteryBlockedMessage('r3_restricted');
    if (message) {
      messages.push(message);
    }
  }

  if (hasTractionBatteryVinNonCompliantError(errorRows)) {
    messages.push({
      key: 'cart.action.add_reference.traction_battery_vin_non_compliant',
      defaultMessage: TRACTION_BATTERY_VIN_NON_COMPLIANT_DEFAULT_MESSAGE,
    });
  }

  return messages;
};

export interface TractionBatteryBlockedMessage {
  key: string;
  defaultMessage: string;
}

export interface TractionBatteryOrderStatus {
  orderBlocked: boolean;
  orderBlockedReason?: TractionBatteryOrderBlockReason;
  orderBlockedMessage?: TractionBatteryBlockedMessage;
}

export const resolveTractionBatteryFamilyCode = (
  ...familyCandidates: Array<string | null | undefined>
): string | undefined => familyCandidates.find((familyCode): familyCode is string => !!familyCode);

export const getTractionBatteryBlockedMessage = (
  reason?: TractionBatteryOrderBlockReason,
): TractionBatteryBlockedMessage | undefined => {
  if (!reason) {
    return undefined;
  }

  if (reason === 'ze_ready_required') {
    return {
      key: 'catalog.reference.order_unavailable.traction_battery_ze_ready_required',
      defaultMessage: 'ZE-Ready homologation is required to purchase this product',
    };
  }

  if (reason === 'vin_required') {
    return {
      key: 'catalog.reference.order_unavailable.traction_battery_vin_required',
      defaultMessage: 'Identify car with VIN to order this part',
    };
  }

  if (reason === 'vin_non_compliant') {
    return {
      key: 'catalog.reference.order_unavailable.traction_battery_vin_non_compliant',
      defaultMessage: TRACTION_BATTERY_VIN_NON_COMPLIANT_DEFAULT_MESSAGE,
    };
  }

  return {
    key: 'catalog.reference.order_unavailable.r3_r3m_traction_battery',
    defaultMessage: 'Not allowed to order this part - please contact your R1',
  };
};

export const getTractionBatteryOrderStatus = ({
  userRoles,
  familyCode,
  vin,
  isCompatible = false,
}: {
  userRoles?: TractionBatteryUserRoles;
  familyCode?: string;
  vin?: string;
  isCompatible?: boolean;
}): TractionBatteryOrderStatus => {
  const orderBlockedReason = getTractionBatteryOrderBlockReason(userRoles, familyCode, vin, isCompatible);
  const orderBlockedMessage = getTractionBatteryBlockedMessage(orderBlockedReason);
  return {
    orderBlockedMessage,
    orderBlockedReason,
    orderBlocked: !!orderBlockedReason,
  };
};
